import { DestroyRef, Directive, inject } from '@angular/core';
import { RegisterModalFormService } from '../services/register-modal-form.service';
import { ToastService } from '@shared/components/toast';
import { REGISTER_TOASTS } from '../constants/register-toasts.constants';
import { RegisterParams } from '@app/core/services/auth/auth.interfaces';
import { AuthService } from '@app/core/services/auth/auth.service';
import { catchError, EMPTY, filter, finalize, switchMap, tap } from 'rxjs';
import { DialogRef } from '@angular/cdk/dialog';
import { RegisterRedirectionsService } from '../services/register-redirections.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormErrorService } from '@shared/components/form-error-modal/form-error.service';
import { extractErrorMessage } from '@shared/utils';

@Directive({
  selector: '[appRegisterSubmit]',
  host: {
    '(ngSubmit)': 'onSubmit()',
  },
})
export class RegisterSubmitDirective {
  private readonly _formService = inject(RegisterModalFormService);
  private readonly _toastService = inject(ToastService);
  private readonly _authService = inject(AuthService);
  private readonly _dialogRef = inject(DialogRef);
  private readonly _registerRedirectionsService = inject(
    RegisterRedirectionsService,
  );
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _formErrorService = inject(FormErrorService);

  private readonly _registerForm = this._formService.registerForm;

  protected onSubmit() {
    if (this._registerForm.invalid) {
      this._registerForm.markAllAsTouched();
      return;
    }

    const normalizedEmail = this._registerForm.controls.email.value
      .toLowerCase()
      .trim();
    const normalizedPassword =
      this._registerForm.controls.password.value.trim();
    const normalizedConfirmPassword =
      this._registerForm.controls.confirm_password?.value.trim();
    const normalizedFirstName =
      this._registerForm.controls.first_name.value.trim();
    const normalizedLastName =
      this._registerForm.controls.last_name.value.trim();
    const normalizedMiddleName =
      this._registerForm.controls.middle_name.value.trim();
    const normalizedPhoneNumber =
      this._registerForm.controls.phone_number.value.trim();
    const normalizedPhonePrefix =
      this._registerForm.controls.prefix_phone.value.trim();

    const registerParams: RegisterParams = {
      email: normalizedEmail,
      password: normalizedPassword,
      confirm_password: normalizedConfirmPassword,
      first_name: normalizedFirstName,
      middle_name: normalizedMiddleName,
      last_name: normalizedLastName,
      phone_number: normalizedPhoneNumber,
      prefix_phone: normalizedPhonePrefix,
      is_firm: this._registerForm.controls.is_firm.value,
    };
    this._formService.setIsSending(true);

    this._authService
      .register(registerParams)
      .pipe(
        tap(() => {
          this._toastService.show(REGISTER_TOASTS['SUCCESS']);
        }),
        switchMap(() =>
          this._authService
            .login({
              username: normalizedEmail,
              password: normalizedPassword,
            })
            .pipe(
              filter((loginSuccess) => !!loginSuccess),
              tap(() => {
                this._toastService.show(REGISTER_TOASTS['AUTO_LOGIN_SUCCESS']);

                this._registerRedirectionsService.redirectBasedInTenant();
              }),
              catchError(() => {
                this._toastService.show(REGISTER_TOASTS['ERROR_LOGIN']);
                return EMPTY;
              }),
              finalize(() => {
                this._dialogRef.close();
              }),
            ),
        ),
        catchError((error) => {
          this._toastService.show(REGISTER_TOASTS['ERROR_REGISTER']);

          this._formErrorService.displaySimpleErrorModal(
            extractErrorMessage(error),
            'Error in Register',
          );

          return EMPTY;
        }),
        finalize(() => {
          this._formService.setIsSending(false);
        }),
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe();
  }
}
